import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppTranslation } from "./TranslationContext";

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import Blockquote from '@tiptap/extension-blockquote';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';

import s from '../Yakuniy.module.css';

export const Yakuniy = () => {
  const { user, userKursData, setUserKursData } = useAuth();
  const { kursId } = useParams();
  const { t } = useAppTranslation();

  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [hasAnswered, setHasAnswered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');

  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [isLinkModalOpen, setLinkModalOpen] = useState(false);
  const [imageValue, setImageValue] = useState('');
  const [linkValue, setLinkValue] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Strike,
      Code,
      Blockquote,
      Image,
      Link.configure({ openOnClick: true }),
    ],
    content: '',
  });

  useEffect(() => {
    if (!editor || !user?.id) return;
  
    const fetchData = async () => {
      try {
        const [questionsRes, answersRes] = await Promise.all([
          fetch(`https://api.rasulova-academy.uz/api/kurs-yozma-savol/?kurs=${kursId}`),
          fetch(`https://api.rasulova-academy.uz/api/yozma-javob/?kurs=${kursId}&user=${user.id}`),
        ]);
  
        const questions = await questionsRes.json();
        const answers = await answersRes.json();
  
        // Если есть хотя бы 1 ответ от пользователя
        if (answers.length > 0) {
          const existingAnswer = answers[0]; // предполагаем, что 1 вопрос – 1 ответ
          setHasAnswered(true);
          setStatus(t('alreadySent'));
  
          if (editor && editor.commands) {
            editor.commands.setContent(existingAnswer.javob);
            editor.setOptions({ editable: false });
          }
  
          const questionMatched = questions.find(q => q.id === existingAnswer.savol);
          setQuestion(questionMatched);
  
          if (existingAnswer.ball !== null) setScore(existingAnswer.ball);
          if (existingAnswer.komment) setComment(existingAnswer.komment);
        } else {
          // Если ответов нет — выбираем случайный вопрос
          if (Array.isArray(questions) && questions.length > 0) {
            const randomIndex = Math.floor(Math.random() * questions.length);
            const selectedQuestion = questions[randomIndex];
            setQuestion(selectedQuestion);
          }
        }
  
      } catch (error) {
        console.error('Ошибка при загрузке:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [editor, user]);
  

  const handleSubmit = async () => {
    if (!editor) return;
    const html = editor.getHTML();

    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.rasulova-academy.uz/api/yozma-javob/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          javob: html,
          kurs: kursId,
          savol: question.id,
          user: user.id,
        }),
      });

      if (response.ok) {
        setStatus(t('sent'));
        setShowModal(true);

        editor.commands.clearContent();

        // Обновляем прогресс
        if (userKursData && parseInt(userKursData.kurs) === parseInt(kursId)) {
          try {
            const patchRes = await fetch(`https://api.rasulova-academy.uz/api/user-kurs/${userKursData.id}/`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user: user.id,
                kurs: parseInt(kursId),
                mavzu: parseInt(userKursData.mavzu, 10) + 1,
              }),
            });

            if (!patchRes.ok) {
              throw new Error('❌ Не удалось обновить прогресс после ответа');
            }

            const updated = await patchRes.json();
            setUserKursData({
              ...userKursData,
              mavzu: updated.mavzu,
            });
          } catch (err) {
            console.error('PATCH error:', err.message);
          }
        }

        setHasAnswered(true);
        editor.setOptions({ editable: false });

      } else {
        setStatus('❌ Ошибка при отправке.');
      }
    } catch (error) {
      console.error('Ошибка соединения:', error);
      setStatus('❌ Ошибка соединения.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const MenuBar = ({ editor }) => {
    if (!editor) return null;

    return (
      <div className={s.menuBar}>
        <button onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></button>
        <button onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></button>
        <button onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></button>
        <button onClick={() => editor.chain().focus().toggleCode().run()}>{'</>'}</button>
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()}>❝</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
        <button onClick={() => editor.chain().focus().toggleBulletList().run()}>• {t('list')}</button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. {t('list')}</button>
        <button onClick={() => editor.chain().focus().unsetAllMarks().run()}>{t('clear')}</button>
        <button onClick={() => setImageModalOpen(true)}>🖼️ {t('enterImg')}</button>
        <button onClick={() => setLinkModalOpen(true)}>🔗 {t('enterLink')}</button>
        <button onClick={() => editor.chain().focus().unsetLink().run()}>❌ {t('removeLink')}</button>
      </div>
    );
  };

  return (
    <div className={s.container}>
      <h2 className={s.title}>{t('yakuniyTitle')}</h2>

      {loading ? (
        <p>Загрузка вопроса...</p>
      ) : (
        <>
          <div className={s.question}>
            <strong>{t('question')}{hasAnswered ? t('youAnswered') : ''}:</strong>
            <div dangerouslySetInnerHTML={{ __html: question?.savol_uz }} />
          </div>

          <MenuBar editor={editor} />

          <div className={s.editorBox}>
            <EditorContent editor={editor} />
          </div>

          <button
            onClick={handleSubmit}
            className={s.submitButton}
            disabled={hasAnswered || isSubmitting}
          >
            {t('sendAnswer')}
          </button>

          {status && <p className={s.statusMessage}>{status}</p>}

          {score !== null && (
            <p className={s.score}><strong>{t('yourRating')}</strong> {score} {t('ball')}</p>
          )}

          {comment && (
            <div className={s.comment}>
              <strong>{t('comment')}</strong>
              <p>{comment}</p>
            </div>
          )}
        </>
      )}

      {isImageModalOpen && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <h3>{t('enterImg')}</h3>
            <input
              type="text"
              value={imageValue}
              onChange={(e) => setImageValue(e.target.value)}
              placeholder={t('enterUrlImg')}
            />
            <div className={s.modalActions}>
              <button
                onClick={() => {
                  editor.chain().focus().setImage({ src: imageValue }).run();
                  setImageValue('');
                  setImageModalOpen(false);
                }}
              >
                {t('enterImg')}
              </button>
              <button onClick={() => setImageModalOpen(false)}>{t('cansel')}</button>
            </div>
          </div>
        </div>
      )}

      {isLinkModalOpen && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <h3>{t('enterLink')}</h3>
            <input
              type="text"
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              placeholder={t('enterUrlLink')}
            />
            <div className={s.modalActions}>
              <button
                onClick={() => {
                  editor.chain().focus().extendMarkRange('link').setLink({ href: linkValue }).run();
                  setLinkValue('');
                  setLinkModalOpen(false);
                }}
              >
                {t('add')}
              </button>
              <button onClick={() => setLinkModalOpen(false)}>{t('cansel')}</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <h3>{t('sent')}</h3>
            <p>{t('enterSoon')}</p>
            <button className={s.submitButton} onClick={() => setShowModal(false)}>{t('close')}</button>
          </div>
        </div>
      )}
    </div>
  );
};
